// Logistics-suite (routes / stops / carriers / carrier-rigs) client
// surface. Carved out of `src/lib/manufacturing/deliveryClient.ts` as
// part of the mfg_* → logi_* schema-rename slice. The underlying API
// surface moved from /api/mfg/{delivery-routes,delivery-stops,...} to
// /api/logi/{routes,stops,carriers,carrier-rigs} -- see vercel.json.
//
// `manufacturingFetch` is reused for the shared Bearer + workspace-org
// wire pattern. Server auth is `requireLogiApiAuth`; entitlement is any
// active anchor (`assertAnyAnchorEntitlementActive`).

import { manufacturingFetch } from "@/lib/manufacturingClient";
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";
import type { MfgUnitsListResponse } from "@/lib/manufacturing/types";
import type {
  LogiCarrierCreateResponse,
  LogiCarrierKind,
  LogiCarrierRigCreateResponse,
  LogiCarrierRigsListResponse,
  LogiCarrierRigUpdateResponse,
  LogiCarriersListResponse,
  LogiCarrierUpdateResponse,
  LogiRigCapacityUnit,
  LogiRouteCreateResponse,
  LogiRouteDetailResponse,
  LogiRoutesListResponse,
  LogiRoutesCalendarResponse,
  LogiRouteStateUpdateResponse,
  LogiRouteUpdateResponse,
  LogiRouteAssignRigResponse,
  LogiStopsReorderResponse,
  LogiRouteState,
  LogiStopAddResponse,
  LogiStopKind,
  LogiStopUpdateResponse,
} from "@/lib/logistics/types";

export function listLogisticsUnits(filters?: {
  plant_id?: string;
  inventory_holder_org_id?: string;
  sale_path?: string;
}) {
  const q = new URLSearchParams();
  if (filters?.plant_id) q.set("plant_id", filters.plant_id);
  if (filters?.inventory_holder_org_id) q.set("inventory_holder_org_id", filters.inventory_holder_org_id);
  if (filters?.sale_path) q.set("sale_path", filters.sale_path);
  const qs = q.toString();
  const path = qs ? `/api/logi/units?${qs}` : "/api/logi/units";
  return manufacturingFetch<MfgUnitsListResponse>(path, { method: "GET" });
}

export function listLogisticsRoutes(filters?: {
  unit_id?: string;
  carrier_id?: string;
  route_state?: LogiRouteState;
  scheduled_on?: string;
  scheduled_window_label?: string;
  origin_stop_kind?: LogiStopKind;
}) {
  const q = new URLSearchParams();
  if (filters?.unit_id) q.set("unit_id", filters.unit_id);
  if (filters?.carrier_id) q.set("carrier_id", filters.carrier_id);
  if (filters?.route_state) q.set("route_state", filters.route_state);
  if (filters?.scheduled_on) q.set("scheduled_on", filters.scheduled_on);
  if (filters?.scheduled_window_label) {
    q.set("scheduled_window_label", filters.scheduled_window_label);
  }
  if (filters?.origin_stop_kind) q.set("origin_stop_kind", filters.origin_stop_kind);
  const qs = q.toString();
  const path = qs ? `/api/logi/routes?${qs}` : "/api/logi/routes";
  return manufacturingFetch<LogiRoutesListResponse>(path, { method: "GET" });
}

export function getLogisticsRoutesCalendar(bounds: { scheduled_from: string; scheduled_to: string }) {
  const q = new URLSearchParams({
    scheduled_from: bounds.scheduled_from,
    scheduled_to: bounds.scheduled_to,
  });
  return manufacturingFetch<LogiRoutesCalendarResponse>(
    `/api/logi/routes/calendar?${q.toString()}`,
    { method: "GET" }
  );
}

export function getLogisticsRouteDetail(routeId: string) {
  const q = new URLSearchParams({ route_id: routeId });
  return manufacturingFetch<LogiRouteDetailResponse>(
    `/api/logi/routes/detail?${q.toString()}`,
    { method: "GET" }
  );
}

export function createLogisticsRoute(payload: {
  unit_id: string;
  carrier_id?: string;
  // D3j: optional rig pick + per-run driver. Backend enforces that the rig
  // belongs to the supplied carrier (400 if mismatched, 404 if cross-org).
  rig_id?: string;
  driver_name?: string;
  route_state?: LogiRouteState;
  scheduled_for?: string;
  // D3k: human-readable scheduling block. Derived from the picker via
  // scheduledForPicker.deriveSchedulingFromDraft; pass undefined for
  // legacy/specific_time mode (display falls back to scheduled_for).
  scheduled_window_label?: string;
  notes?: string;
}) {
  return manufacturingFetch<LogiRouteCreateResponse>("/api/logi/routes/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// D3j: assignment-edit -- mutate carrier_id, rig_id, driver_name,
// scheduled_for, notes on an existing route. null clears, undefined leaves
// alone, string sets (same three-way pattern as carriers + stops update).
// route_state has its own transition endpoint (below).
export function updateLogisticsRoute(payload: {
  route_id: string;
  carrier_id?: string | null;
  rig_id?: string | null;
  driver_name?: string | null;
  scheduled_for?: string | null;
  // D3k: clear by passing null (e.g. when switching from a labeled mode
  // back to specific_time).
  scheduled_window_label?: string | null;
  notes?: string;
}) {
  return manufacturingFetch<LogiRouteUpdateResponse>("/api/logi/routes/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function assignLogisticsRouteToRig(payload: { route_id: string; rig_id: string }) {
  return manufacturingFetch<LogiRouteAssignRigResponse>("/api/logi/routes/assign-rig", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function reorderLogisticsRouteStops(payload: {
  route_id: string;
  ordered_stop_ids: string[];
}) {
  return manufacturingFetch<LogiStopsReorderResponse>("/api/logi/stops/reorder", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateLogisticsRouteState(payload: {
  route_id: string;
  route_state: LogiRouteState;
  // Optional explicit completion timestamp. The service stamps now() on
  // delivered when this is omitted; pass null to suppress that (e.g. for
  // a future "mark delivered without timestamp" backfill flow).
  completed_at?: string | null;
}) {
  return manufacturingFetch<LogiRouteStateUpdateResponse>("/api/logi/routes/state", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function addLogisticsStop(payload: {
  route_id: string;
  stop_kind: LogiStopKind;
  sequence_index?: number | null;
  recipient_org_id?: string;
  recipient_contact_id?: string;
  address?: Record<string, unknown>;
  evidence_requirements?: unknown[];
  scheduled_window_start?: string;
  scheduled_window_end?: string;
  notes?: string;
}) {
  return manufacturingFetch<LogiStopAddResponse>("/api/logi/stops/add", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// D3g per-stop update. All clearable fields accept null on top of the
// usual string -- null = clear, undefined = leave alone, string = set
// (matches the three-way pattern in updateLogisticsRouteState +
// updateLogisticsCarrier). The backend whitelists this set; see
// server/logi-services/stopsService.updateOrgStop for the exclusion
// notes on stop_kind / sequence_index / route_id /
// evidence_requirements / metadata.
export function updateLogisticsStop(payload: {
  stop_id: string;
  recipient_org_id?: string | null;
  recipient_contact_id?: string | null;
  address?: Record<string, unknown> | null;
  scheduled_window_start?: string | null;
  scheduled_window_end?: string | null;
  arrived_at?: string | null;
  departed_at?: string | null;
  evidence_url?: string | null;
  evidence_completed_at?: string | null;
  notes?: string;
}) {
  return manufacturingFetch<LogiStopUpdateResponse>("/api/logi/stops/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listLogisticsCarriers(filters?: {
  carrier_kind?: LogiCarrierKind;
  is_active?: boolean;
}) {
  const q = new URLSearchParams();
  if (filters?.carrier_kind) q.set("carrier_kind", filters.carrier_kind);
  if (filters?.is_active !== undefined) q.set("is_active", filters.is_active ? "true" : "false");
  const qs = q.toString();
  const path = qs ? `/api/logi/carriers?${qs}` : "/api/logi/carriers";
  return manufacturingFetch<LogiCarriersListResponse>(path, { method: "GET" });
}

export function createLogisticsCarrier(payload: {
  name: string;
  carrier_kind?: LogiCarrierKind;
  linked_org_id?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  coverage_areas?: string[];
  capacity_linear_feet?: number;
  insurance_expires_at?: string;
  contract_expires_at?: string;
  is_active?: boolean;
  notes?: string;
}) {
  return manufacturingFetch<LogiCarrierCreateResponse>("/api/logi/carriers/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// Update accepts null on null-clearable fields so the caller can clear a
// previously-set value. Pass undefined to leave the field unchanged.
export function updateLogisticsCarrier(payload: {
  carrier_id: string;
  name?: string;
  carrier_kind?: LogiCarrierKind;
  linked_org_id?: string | null;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  coverage_areas?: string[];
  capacity_linear_feet?: number | null;
  insurance_expires_at?: string | null;
  contract_expires_at?: string | null;
  is_active?: boolean;
  notes?: string;
}) {
  return manufacturingFetch<LogiCarrierUpdateResponse>("/api/logi/carriers/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// D3j: carrier-rigs (per-carrier fleet) client surface. Backend enforces
// carrier ownership (the rig must be created under a carrier in the same
// org) + the capacity-pair rule (both capacity_value and capacity_unit
// must be set together, or both omitted).
export function listLogisticsCarrierRigs(filters?: {
  carrier_id?: string;
  is_active?: boolean;
}) {
  const q = new URLSearchParams();
  if (filters?.carrier_id) q.set("carrier_id", filters.carrier_id);
  if (filters?.is_active !== undefined) q.set("is_active", filters.is_active ? "true" : "false");
  const qs = q.toString();
  const path = qs ? `/api/logi/carrier-rigs?${qs}` : "/api/logi/carrier-rigs";
  return manufacturingFetch<LogiCarrierRigsListResponse>(path, { method: "GET" });
}

export function createLogisticsCarrierRig(payload: {
  carrier_id: string;
  designation: string;
  standard_driver_name?: string;
  capacity_value?: number;
  capacity_unit?: LogiRigCapacityUnit;
  notes?: string;
  is_active?: boolean;
}) {
  return manufacturingFetch<LogiCarrierRigCreateResponse>("/api/logi/carrier-rigs/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// Update accepts null on standard_driver_name + capacity pair so the caller
// can clear a previously-set value. The capacity pair must move together --
// passing one null without the other yields a 400.
export function updateLogisticsCarrierRig(payload: {
  rig_id: string;
  designation?: string;
  standard_driver_name?: string | null;
  capacity_value?: number | null;
  capacity_unit?: LogiRigCapacityUnit | null;
  notes?: string;
  is_active?: boolean;
}) {
  return manufacturingFetch<LogiCarrierRigUpdateResponse>("/api/logi/carrier-rigs/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function emailLogisticsRouteManifest(payload: { route_id: string; to_email: string }) {
  return manufacturingFetch<{ ok: true; emailed_to: string }>("/api/logi/routes/email-manifest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function downloadLogisticsRouteManifestPdf(routeId: string): Promise<
  { ok: true } | { ok: false; error: string }
> {
  if (!supabase) return { ok: false, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: "Sign in required." };

  const headers: HeadersInit = { Authorization: `Bearer ${token}` };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(
    `/api/logi/routes/manifest-pdf?route_id=${encodeURIComponent(routeId)}`,
    { method: "GET", headers }
  );
  if (!r.ok) {
    const text = await r.text();
    let err = text;
    try {
      const j = JSON.parse(text) as { error?: string };
      if (j.error) err = j.error;
    } catch {
      /* ignore */
    }
    return { ok: false, error: err || r.statusText };
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `route-manifest-${routeId.slice(0, 8)}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
  return { ok: true };
}
